import { Link } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import bloom250Img from "../assets/images/BLOOM250SOLITO.png";

const WHATSAPP_NUMBER = "51994692096";
function waLink(message) {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
}

function Divider({ tone = "light" }) {
  return (
    <div className="mx-auto max-w-7xl px-6 py-14">
      <div className={tone === "dark" ? "h-px bg-white/10" : "h-px bg-black/5"} />
    </div>
  );
}


/** Reveal suave sin librerías */
function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShow(true);
          obs.disconnect();
        }
      },
      { threshold: 0.14 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={[
        "transition-all duration-700 ease-out will-change-transform",
        show ? "opacity-100 translate-y-0 blur-0" : "opacity-0 translate-y-8 blur-[8px]",
        className,
      ].join(" ")}
    >
      {children}
    </div>
  );
}

/** Parallax mini (sin librerías) */
function useParallax(strength = 22) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onScroll = () => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const progress = 1 - Math.min(Math.max((r.top + r.height * 0.2) / vh, 0), 1);
      const y = (progress - 0.5) * strength * 2; // -strength..+strength
      el.style.transform = `translate3d(0, ${y}px, 0)`;
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [strength]);

  return ref;
}

/** Sticky stepper Apple-like (texto cambia al scroll) */
function StickyStory({ steps }) {
  const wrapRef = useRef(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    const nodes = Array.from(wrap.querySelectorAll("[data-step]"));
    const obs = new IntersectionObserver(
      (entries) => {
        // Tomamos el que esté más visible (más ratio)
        const best = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (best) setActive(Number(best.target.getAttribute("data-step")));
      },
      { threshold: [0.25, 0.5, 0.75] }
    );

    nodes.forEach((n) => obs.observe(n));
    return () => obs.disconnect();
  }, []);

  const current = steps[active] || steps[0];

  return (
    <section className="bg-white">
      <div className="mx-auto max-w-7xl px-6 py-20 md:py-28">
        <div className="grid gap-12 md:grid-cols-12 md:items-start">
          {/* Sticky copy */}
          <div className="md:col-span-5 md:sticky md:top-24">
            <Reveal>
              <p className="text-xs font-semibold tracking-[0.18em] text-slate-500">
                EN EL RESULTADO FINAL
              </p>
              <h2 className="mt-5 text-4xl md:text-6xl font-semibold tracking-tight">
                {current.titleA} <span className="text-slate-400">{current.titleB}</span>
              </h2>
              <p className="mt-8 text-lg leading-relaxed text-slate-600">
                {current.body}
              </p>

              {/* Indicador minimal */}
              <div className="mt-10 flex items-center gap-2">
                {steps.map((_, i) => (
                  <span
                    key={i}
                    className={[
                      "h-1.5 rounded-full transition-all",
                      i === active ? "w-10 bg-slate-900/70" : "w-3 bg-black/10",
                    ].join(" ")}
                  />
                ))}
              </div>
            </Reveal>
          </div>

          {/* Scroll area */}
          <div ref={wrapRef} className="md:col-span-7 space-y-20 md:space-y-28">
            {steps.map((s, i) => (
              <div
                key={i}
                data-step={i}
                className="min-h-[52vh] md:min-h-[60vh] flex items-end"
              >
                <div className="w-full">
                  <Reveal delay={60}>
                    <p className="text-xs font-semibold tracking-[0.18em] text-slate-500">
                      {s.kicker}
                    </p>
                    <div className="mt-4 text-2xl md:text-3xl font-semibold tracking-tight">
                      {s.headline}
                    </div>
                    <p className="mt-4 max-w-2xl text-base md:text-lg leading-relaxed text-slate-600">
                      {s.detail}
                    </p>

                    {/* línea tipo Apple (sin cajitas) */}
                    <div className="mt-10 h-px bg-black/5" />
                  </Reveal>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Bloom250() {
  const waMsg =
    "Hola, deseo cotizar Gelatina Bloom 250. Cantidad: __ kg. Aplicación/uso: __. Proceso: __. Ciudad: __. (Si aplica: documentación FT/MSDS: __).";

  const parallaxRef = useParallax(26);

  const steps = useMemo(
    () => [
      {
        titleA: "Más fuerza",
        titleB: "en cada lote.",
        body:
          "Bloom 250 está dentro del rango de Bloom alto, usado cuando priorizas estructura firme y consistencia en el gel.",
        kicker: "TEXTURA",
        headline: "Estructura definida",
        detail:
          "Cuando la forma y el cuerpo no son negociables. Bloom alto se asocia a mayor fuerza del gel bajo medición estándar.",
      },
      {
        titleA: "Más control",
        titleB: "en proceso.",
        body:
          "Un gel que responde de forma predecible ayuda a reducir ajustes y a mantener un estándar de producción.",
        kicker: "PROCESO",
        headline: "Comportamiento predecible",
        detail:
          "Útil para procesos donde la repetibilidad importa: preparación, reposo y almacenamiento con el mismo objetivo de textura.",
      },
      {
        titleA: "Más soporte",
        titleB: "en el acabado.",
        body:
          "Pensado para aplicaciones donde el gel aporta soporte y estabilidad a la experiencia final del producto.",
        kicker: "ACABADO",
        headline: "Estabilidad de textura",
        detail:
          "Cuando buscas una textura firme, definida y estable. La elección final depende de formulación y condiciones de proceso.",
      },
    ],
    []
  );

  return (
    <main className="bg-white text-slate-900">
      {/* ========= HERO (full-bleed / Apple-like) ========= */}
      <section className="relative overflow-hidden">
        {/* fondo suave */}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(59,130,246,0.10),transparent_45%),radial-gradient(circle_at_80%_30%,rgba(14,165,233,0.10),transparent_45%),radial-gradient(circle_at_55%_85%,rgba(253,230,138,0.10),transparent_55%)]" />

        <div className="relative mx-auto max-w-7xl px-6 pt-10 md:pt-14 pb-16 md:pb-24">
          <div className="flex items-center justify-between">
            <Link to="/gelatinas" className="text-sm font-medium text-slate-600 hover:text-slate-900">
              ← Volver a Gelatinas
            </Link>

            <a
              href={waLink(waMsg)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex rounded-full bg-blue-600 px-6 py-3 text-[14px] font-medium text-white shadow-sm transition hover:bg-blue-700 active:scale-[0.99]"
            >
              Comprar por WhatsApp
            </a>
          </div>

          <div className="mt-14 grid gap-10 md:grid-cols-12 md:items-center">
            <div className="md:col-span-5">
              <Reveal>
                <p className="text-xs font-semibold tracking-[0.18em] text-slate-500">
                  GELATINA INDUSTRIAL
                </p>

                <h1 className="mt-4 text-5xl md:text-7xl font-semibold tracking-tight">
                  Bloom 250
                </h1>

                <p className="mt-8 max-w-xl text-lg md:text-xl leading-relaxed text-slate-600">
                  Bloom alto para formulaciones que buscan{" "}
                  <span className="text-slate-900 font-medium">estructura firme</span>{" "}
                  y un comportamiento consistente del gel.
                </p>

                {/* dos líneas elegantes, sin cajitas */}
                <div className="mt-12 space-y-8">
                  <div className="grid gap-8 sm:grid-cols-2">
                    <div>
                      <p className="text-xs font-semibold tracking-[0.18em] text-slate-500">
                        BLOOM
                      </p>
                      <p className="mt-2 text-base leading-relaxed text-slate-700">
                        El Bloom es una medida estándar de fuerza del gel.
                      </p>
                    </div>

                    <div>
                      <p className="text-xs font-semibold tracking-[0.18em] text-slate-500">
                        DOCUMENTACIÓN
                      </p>
                      <p className="mt-2 text-base leading-relaxed text-slate-700">
                        FT y MSDS disponibles.
                      </p>
                    </div>
                  </div>

                  <div className="h-px bg-black/5" />

                  <div className="grid gap-8 sm:grid-cols-2">
                    <div>
                      <p className="text-xs font-semibold tracking-[0.18em] text-slate-500">
                        SOLUBILIDAD
                      </p>
                      <p className="mt-2 text-base leading-relaxed text-slate-700">
                        Soluble en agua; se integra en múltiples sistemas de formulación.
                      </p>
                    </div>

                    <div>
                      <p className="text-xs font-semibold tracking-[0.18em] text-slate-500">
                        COMPATIBILIDAD
                      </p>
                      <p className="mt-2 text-base leading-relaxed text-slate-700">
                        Compatible con la mayoría de hidrocoloides y componentes de receta.
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>

            {/* Imagen grande (sin borde) */}
            <div className="md:col-span-7">
              <Reveal delay={80}>
                <div className="relative">
                  <div className="absolute -inset-10 bg-black/5 blur-3xl opacity-30" />
                  <div className="relative overflow-hidden rounded-[38px]">
                    {/* capa para parallax */}
                    <div ref={parallaxRef} className="h-[520px] md:h-[640px] lg:h-[720px]">
                      <img
                        src={bloom250Img}
                        alt="Gelatina Bloom 250"
                        className="h-full w-full object-cover object-center"
                        draggable="false"
                      />
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <Divider />

      {/* ========= Sticky storytelling (Apple scroll feel) ========= */}
      <StickyStory steps={steps} />

      {/* ========= Band oscuro (solo 2 frases, full aire) ========= */}
      <section className="bg-slate-950 text-white">
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-32">
          <Reveal>
            <p className="text-xs font-semibold tracking-[0.18em] text-white/60">
              ENFOQUE PROFESIONAL
            </p>
            <h3 className="mt-6 text-4xl md:text-6xl font-semibold tracking-tight">
              Calidad que se siente.
              <span className="text-white/35"> Consistencia que se mantiene.</span>
            </h3>

            <p className="mt-10 max-w-3xl text-lg md:text-xl leading-relaxed text-white/70">
              Si necesitas un resultado firme y estable, Bloom alto suele ser el punto de partida.
              La formulación final se ajusta a tu proceso y a tu objetivo de textura.
            </p>
          </Reveal>
        </div>

        <div className="mx-auto max-w-7xl px-6 pb-20">
          <div className="h-px bg-white/10" />
        </div>
      </section>

      {/* ========= Horizontal scroll-snap (cool, Apple-like) ========= */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 py-20 md:py-28">
          <Reveal>
            <p className="text-xs font-semibold tracking-[0.18em] text-slate-500">
              APLICACIONES (REFERENCIA)
            </p>
            <h3 className="mt-5 text-4xl md:text-6xl font-semibold tracking-tight">
              Cuando buscas más estructura.
            </h3>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-slate-600">
              Ejemplos generales para Bloom alto. La elección final depende de receta, concentración
              y condiciones de proceso.
            </p>
          </Reveal>

          {/* scroll horizontal elegante */}
          <Reveal delay={80} className="mt-14">
            <div
              className="flex gap-8 overflow-x-auto pb-6 snap-x snap-mandatory"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <style>{`
                .no-scrollbar::-webkit-scrollbar{display:none;}
              `}</style>

              <div className="no-scrollbar flex gap-8 overflow-x-auto pb-2 snap-x snap-mandatory">
                {[
                  {
                    t: "Confitería",
                    d: "Formulaciones gelificadas donde el soporte y la definición de forma son clave.",
                  },
                  {
                    t: "Postres / geles",
                    d: "Texturas firmes con estabilidad en el acabado, según receta y proceso.",
                  },
                  {
                    t: "Industrial",
                    d: "Aplicaciones donde se busca desempeño gelificante/estabilizante consistente.",
                  },
                ].map((it, i) => (
                  <div
                    key={it.t}
                    className="min-w-[86%] sm:min-w-[520px] md:min-w-[640px] snap-start"
                  >
                    <div className="rounded-[34px] bg-gradient-to-b from-slate-50 to-white border border-black/5 px-10 py-12 md:px-12 md:py-14">
                      <p className="text-xs font-semibold tracking-[0.18em] text-slate-500">
                        {String(i + 1).padStart(2, "0")}
                      </p>
                      <div className="mt-5 text-3xl md:text-4xl font-semibold tracking-tight">
                        {it.t}
                      </div>
                      <p className="mt-6 text-lg leading-relaxed text-slate-600">
                        {it.d}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          
        </div>
      </section>
    </main>
  );
}
